import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { ButtonStyled } from './CheckboxList.styled';
import { useFeatures } from 'contexts/FeaturesContext';
import { useTickets } from 'contexts/TicketsContext';
import { FeaturesNamesE } from 'declarations/enums/features';
import { ScheduleFilterValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import useTicketsRefinement from 'pages/TicketsPage/components/TicketsRefinement/hooks/useTicketsRefinement';

type DropdownCheckboxListT = {
  shouldFiltersReset: number;
  onScheduleSelect: (isSelected: boolean) => void;
};

export const DropdownCheckboxList: React.FC<DropdownCheckboxListT> = ({
  shouldFiltersReset,
  onScheduleSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checked, setChecked] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number | null>(null);
  const {
    selectedRoofTop,
    updateSearchParamsArray,
    setActiveScheduleCards,
    setActiveScheduleFilters,
    activeScheduleFilters,
  } = useTickets();
  const { isScheduleSelected, setIsScheduleSelected } = useTicketsRefinement();
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(true, selectedRoofTop);
  const [items, setItems] = useState<TicketRequestCategoryI[]>([]);
  const { isFeatureActive } = useFeatures();
  const isScheduleShown = isFeatureActive(FeaturesNamesE.Schedule);

  /**
   * Handlers
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (categoryId: number) => () => {
    const currentIndex = checked.indexOf(categoryId);
    let newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(categoryId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    const allSelected = newChecked.length === items.length;
    setIsAllSelected(allSelected);

    updateSearchParamsArray(TicketsRefinementKeysE.Category, newChecked);
    const isAnyScheduleSelected = newChecked.some(id => id === scheduleCategoryId);
    setActiveScheduleCardsAndFilters(isAnyScheduleSelected);
    onScheduleSelect(isAnyScheduleSelected);
    setIsScheduleSelected(isAnyScheduleSelected);

    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setIsAllSelected(false);
      setChecked([]);
      updateSearchParamsArray(TicketsRefinementKeysE.Category, []);
      updateSearchParamsArray(TicketsRefinementKeysE.Schedule, []);
      setActiveScheduleCards([]);
      setActiveScheduleCardsAndFilters(false);
      setIsScheduleSelected(false);
    } else {
      const allIds = items.map(item => item.id);
      setIsAllSelected(true);
      setChecked(allIds);
      updateSearchParamsArray(TicketsRefinementKeysE.Category, allIds);
      if (scheduleCategoryId !== null) {
        setActiveScheduleFilters([
          ScheduleFilterValuesE.LessThan31Days,
          ScheduleFilterValuesE.From31To60Days,
          ScheduleFilterValuesE.From61To90Days,
          ScheduleFilterValuesE.From91To120Days,
          ScheduleFilterValuesE.Over120Days,
        ]);
        setIsScheduleSelected(true); // Select schedule
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, activeScheduleFilters);
      }
    }
  };

  const setActiveScheduleCardsAndFilters = useCallback(
    (isActive: boolean) => {
      if (isActive) {
        setActiveScheduleCards([
          'schedule-all',
          'dealer-1',
          'dealer-2',
          'dealer-3',
          'dealer-4',
          'dealer-5',
        ]);
        setActiveScheduleFilters([
          ScheduleFilterValuesE.LessThan31Days,
          ScheduleFilterValuesE.From31To60Days,
          ScheduleFilterValuesE.From61To90Days,
          ScheduleFilterValuesE.From91To120Days,
          ScheduleFilterValuesE.Over120Days,
        ]);
      } else {
        setActiveScheduleCards([]);
        setActiveScheduleFilters([]);
      }
    },
    [setActiveScheduleCards, setActiveScheduleFilters]
  );

  /**
   * Effects
   */
  useEffect(() => {
    setChecked([]);
    setIsAllSelected(false);
    setIsScheduleSelected(false); // Reset on filters reset
  }, [shouldFiltersReset, setIsScheduleSelected]);

  useEffect(() => {
    if (isAllSelected && !isScheduleSelected && scheduleCategoryId) {
      setChecked(prev => prev.filter(id => id !== scheduleCategoryId));
      setIsAllSelected(false);
    }
  }, [isScheduleSelected, isAllSelected, scheduleCategoryId]);

  useEffect(() => {
    if (ticketRequestCategoriesResult.data) {
      setItems(ticketRequestCategoriesResult.data);
      const scheduleCategory = ticketRequestCategoriesResult.data.find(
        item => item.categoryName.toLowerCase() === 'schedule'
      );
      setScheduleCategoryId(scheduleCategory ? scheduleCategory.id : null);

      // Check if all items are selected
      const searchParams = new URLSearchParams(window.location.search);
      const categoryIds =
        searchParams.get(TicketsRefinementKeysE.Category)?.split(',').map(Number) || [];

      // Determine if "All" should be selected
      const allCategoriesSelected =
        searchParams.get(TicketsRefinementKeysE.Category) === null ||
        (categoryIds.length > 0 &&
          ticketRequestCategoriesResult.data.every(item => categoryIds.includes(item.id)));

      setChecked(categoryIds);

      setIsAllSelected(allCategoriesSelected && categoryIds.length > 0);

      const isAnyScheduleSelected = categoryIds.some(id => id === scheduleCategoryId);
      setIsScheduleSelected(isAnyScheduleSelected);
      setActiveScheduleCardsAndFilters(isAnyScheduleSelected);
    } else {
      setItems([]);
      setChecked([]);
      setIsAllSelected(false);
      setScheduleCategoryId(null);
    }
  }, [
    ticketRequestCategoriesResult.data,
    selectedRoofTop,
    setIsScheduleSelected,
    scheduleCategoryId,
    setActiveScheduleCardsAndFilters,
  ]);

  useEffect(() => {
    if (scheduleCategoryId !== null) {
      if (isScheduleSelected && !checked.includes(scheduleCategoryId)) {
        setChecked(prev => [...prev, scheduleCategoryId]);
      } else if (!isScheduleSelected && checked.includes(scheduleCategoryId)) {
        setChecked(prev => prev.filter(id => id !== scheduleCategoryId));
      }
    }
  }, [isScheduleSelected, scheduleCategoryId, checked]);

  useEffect(() => {
    if (!selectedRoofTop || !isScheduleShown) {
      // If no rooftop is selected or Schedule feature is not active,
      // remove the schedule category and subcategories from search params
      updateSearchParamsArray(TicketsRefinementKeysE.Category, []);
      updateSearchParamsArray(TicketsRefinementKeysE.Schedule, []);
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, []);
      setChecked([]);
      setIsAllSelected(false);
      setActiveScheduleCards([]);
      setActiveScheduleFilters([]);
      setIsScheduleSelected(false);
    }
  }, [
    selectedRoofTop,
    isScheduleShown,
    updateSearchParamsArray,
    setActiveScheduleCards,
    setActiveScheduleFilters,
    setIsScheduleSelected,
  ]);

  if (ticketRequestCategoriesResult.isLoading || !selectedRoofTop) {
    return (
      <ButtonStyled variant="outlined" disabled>
        Categories
      </ButtonStyled>
    );
  }

  if (ticketRequestCategoriesResult.error) {
    return <div>Error loading categories</div>;
  }

  return (
    <div>
      <Tooltip
        title={
          isAllSelected
            ? 'All'
            : checked.map(id => items.find(item => item.id === id)?.categoryName).join(', ') ||
              'Select Category'
        }
        placement="top"
      >
        <ButtonStyled
          aria-controls="checkbox-list-menu"
          aria-haspopup="true"
          variant="outlined"
          onClick={handleClick}
          style={{
            maxWidth: '140px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'start',
          }}
        >
          {isAllSelected
            ? 'All'
            : checked.map(id => items.find(item => item.id === id)?.categoryName).join(', ') ||
              'Select Category'}
        </ButtonStyled>
      </Tooltip>
      <Menu
        id="checkbox-list-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSelectAll}>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isAllSelected}
              indeterminate={checked.length > 0 && checked.length < items.length}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>
        {items.map(category => (
          <MenuItem key={category.id} onClick={handleToggle(category.id)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={checked.includes(category.id) || isAllSelected}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={category.categoryName} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default DropdownCheckboxList;
